import { useSearchParams } from 'react-router';
import { Button, PageShell, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import { CASH_RECEIPT_WRITE_ROLES } from '../api/cashReceiptApi';
import { CashReceiptListTab } from '../components/CashReceiptListTab';
import { CashReceiptRegisterTab } from '../components/CashReceiptRegisterTab';
import { useCashReceiptLabels } from '../lib/cashReceiptLib';

/*
 * 주문·배송 > 현금영수증 관리 — 고도몰 화면 2개(발급/조회·개별발급)를 탭으로. 탭은 ?tab= 로 보존.
 * 주문별 상세·부분 취소는 주문 상세의 현금영수증 카드(features/orders/components/OrderCashReceiptSection).
 */

const TABS = ['list', 'register'] as const;
type TabKey = (typeof TABS)[number];
const isTab = (v: string | null): v is TabKey => v !== null && (TABS as ReadonlyArray<string>).includes(v);

export const CashReceiptPage = () => {
  const { t } = useCashReceiptLabels();
  const canWrite = useHasRole(CASH_RECEIPT_WRITE_ROLES);
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const active: TabKey = isTab(raw) && (raw !== 'register' || canWrite) ? raw : 'list';
  const label: Record<TabKey, string> = {
    list: t('orders.cashReceiptAdmin.tabs.list', '현금영수증 발급/조회'),
    register: t('orders.cashReceiptAdmin.tabs.register', '현금영수증 개별발급'),
  };
  const go = (key: TabKey) => {
    const next = new URLSearchParams(params);
    next.set('tab', key);
    setParams(next, { replace: true });
  };
  const tabs = canWrite ? TABS : (['list'] as const);

  return (
    <PageShell
      title={t('orders.cashReceiptAdmin.title', '현금영수증 관리')}
      breadcrumb={[{ label: t('orders.taxInvoice.group', '주문·배송') }, { label: t('orders.cashReceiptAdmin.title', '현금영수증 관리') }, { label: label[active] }]}
      actions={
        canWrite && active === 'list' ? (
          <Button variant="danger" size="sm" onClick={() => go('register')} data-testid="cash-receipt-open-register">
            {label.register}
          </Button>
        ) : null
      }
      data-testid="cash-receipt-page"
    >
      <Tabs value={active} onValueChange={(key) => go(key as TabKey)}>
        <TabsList aria-label={t('orders.cashReceiptAdmin.title', '현금영수증 관리')}>
          {tabs.map((tab) => (
            <TabsTrigger key={tab} value={tab} data-testid={`cash-receipt-tab-${tab}`}>
              {label[tab]}
            </TabsTrigger>
          ))}
        </TabsList>
        {/* 활성 탭만 마운트 — 쓰지 않는 목록 조회를 막는다. */}
        <TabsContent value="list">{active === 'list' ? <CashReceiptListTab /> : null}</TabsContent>
        {canWrite ? (
          <TabsContent value="register">{active === 'register' ? <CashReceiptRegisterTab onIssued={() => go('list')} /> : null}</TabsContent>
        ) : null}
      </Tabs>
    </PageShell>
  );
};
